import axios, { AxiosResponse } from 'axios';

/**
 * @description API 回應
 * @property {number} id - 使用者ID
 * @property {string} account - 帳號
 * @property {string} identityNumber - 身份證號
 * @property {string} name - 姓名
 * @property {string} birthday - 生日
 * @property {string} phone - 手機號碼
 * @property {string} [tel] - 市話
 * @property {string} [email] - 電子郵件
 * @property {'Male' | 'Female' | 'Other'} sex - 性別
 * @property {boolean} isCooperatorMember - 是否為合作會員
 * @property {number} cooperatorId - 合作ID
 * @property {string} cooperatorName - 合作名稱
 * @property {Object} userCertification - 使用者認證
 * @property {boolean} userCertification.generalMember - 一般會員
 * @property {boolean} userCertification.identityCertifiedMember - 身份認證會員
 * @property {boolean} userCertification.medicalSystemCertifiedMember - 醫療系統認證會員
 * @property {boolean} userCertification.hrc - HRC
 * @property {Array<Object>} roles - 使用者角色
 * @property {string} roles.roleCode - 角色代碼
 * @property {string} roles.roleName - 角色名稱
 * @property {number} roles.hospitalOrganizationId - 醫療組織ID
 * @property {string} roles.hospitalOrganizationCode - 醫療組織代碼
 * @property {string} roles.hospitalOrganizationName - 醫療組織名稱
 * @property {string | null} roles.serviceLocationCode - 服務地點代碼
 * @property {boolean | null} roles.canUseWebService - 可使用網路服務
 * @property {Object} [userDetail] - 使用者詳情
 * @property {boolean} userDetail.informationConsent - 資訊同意
 * @property {boolean} [userDetail.healthPassbook] - 健康手冊
 * @property {boolean} [userDetail.agreeOrganizationSwitchData] - 同意組織切換數據
 * @property {Array<Object>} userAddresses - 使用者地址
 * @property {string} userAddresses.city - 城市
 * @property {string} userAddresses.district - 區域
 * @property {string} userAddresses.street - 街道
 * @property {number} userAddresses.kind - 地址類型
 * @property {Array<Object>} [thirdPartyBindings] - 第三方綁定
 * @property {'Line' | 'Google' | 'Facebook' | 'Apple'} thirdPartyBindings.type - 綁定類型
 * @property {number} [thirdPartyBindings.id] - 綁定ID
 * @property {boolean} [thirdPartyBindings.hasBound] - 是否已綁定
 */
export interface TResponse {
    id: number;
    account: string;
    identityNumber: string;
    name: string;
    birthday: string;
    phone: string;
    tel?: string;
    email?: string;
    sex: 'Male' | 'Female' | 'Other';
    isCooperatorMember: boolean;
    cooperatorId: number;
    cooperatorName: string;
    userCertification: {
        generalMember: boolean;
        identityCertifiedMember: boolean;
        medicalSystemCertifiedMember: boolean;
        hrc: boolean;
    };
    roles: {
        roleCode: string;
        roleName: string;
        hospitalOrganizationId: number;
        hospitalOrganizationCode: string;
        hospitalOrganizationName: string;
        serviceLocationCode: string;
        canUseWebService: boolean;
    }[];
    userDetail: {
        informationConsent: boolean;
        healthPassbook?: boolean;
        agreeOrganizationSwitchData?: boolean;
    };
    userAddresses: {
        city: string;
        district: string;
        street: string;
        kind: number;
    }[];
    thirdPartyBindings: {
        type: 'Line' | 'Google' | 'Facebook' | 'TYPassCard' | 'Apple';
        id?: number;
        hasBound?: boolean;
    }[];
}

/**
 * 取得使用者profile
 * @returns {Promise<AxiosResponse<TResponse, null>>} - 回應物件
 */
export default async function apiUserProfile() {
    const response: AxiosResponse<TResponse, null> = await axios.request({
        url: '/api/User/profile',
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    });

    return response;
}
